import {
  Alert,
  CardMedia,
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine,
  Typography,
} from '@one-thd/sui-atomic-components';
import React, { JSX } from 'react';
import { ERR_GENERIC } from '../../../constants/response-error-messages';
import ILineItem from '../../../models/line-item';
import { IFulfillmentGroup } from '../../../models/order-details';
import { IExpandedTableRow } from '../../../models/purchase-history/table';
import { generateImageUrl } from '../../../utils/general';
import { separateReturnedFG } from '../../../utils/returns/returns-fg-helper';
import { dateFormat } from '../../order-details/util/format';
import { PHOrderDetailsAndActions } from '../delivery-tracker/PHOrderDetailsAndActions';

export const ExpandedTableRow = ({
  orderDetailsData,
  orderDetailsError,
  orderDetailsLoaded = false,
}: IExpandedTableRow): JSX.Element => {
  if (orderDetailsError) {
    return (
      <div style={{ width: '880px' }}>
        <Alert status="error" size="large">
          {ERR_GENERIC.message}
        </Alert>
      </div>
    );
  }

  if (orderDetailsLoaded === false) {
    return (
      <div
        className="sui-flex sui-flex-col sui-ml-[16px] sui-p-4"
        style={{ border: '1px solid #CCCCCB', minWidth: '880px' }}
      >
        <Skeleton disablePadding orientation="horizontal">
          <SkeletonContent
            disablePadding
            initialSize="2/4"
            style={{ width: '195px', height: '50px' }}
          >
            <SkeletonLine variant="single" />
            <SkeletonLine variant="single" />
          </SkeletonContent>
          <SkeletonContent
            disablePadding
            initialSize="1/4"
            style={{ width: '50px', height: '50px' }}
          >
            <SkeletonBlock />
          </SkeletonContent>
        </Skeleton>
      </div>
    );
  }

  const { orderedGroups } = separateReturnedFG(
    orderDetailsData?.fulfillmentGroups,
  );

  const canceledLineItems = orderedGroups.flatMap((fg) =>
    fg.lineItems && fg.cancellationStatus !== 'OUT_OF_STOCK'
      ? fg.lineItems.filter((item) => item.statusDescription === 'Canceled')
      : [],
  );

  return (
    <>
      <div
        className="sui-flex sui-gap-[16px] sui-flex-col sui-ml-[16px] sui-p-4"
        style={{ border: '1px solid #CCCCCB', minWidth: '880px' }}
      >
        {orderedGroups?.map(
          (fulfillment: IFulfillmentGroup | ILineItem | null, index) => {
            if (
              (fulfillment as IFulfillmentGroup)?.lineItems &&
              (fulfillment as IFulfillmentGroup)?.cancellationStatus !==
                'OUT_OF_STOCK' &&
              (fulfillment as IFulfillmentGroup)?.lineItems?.every(
                (item) => item.statusDescription === 'Canceled',
              )
            ) {
              return null;
            }
            return (
              <div
                className="sui-flex sui-w-full"
                style={{
                  minHeight: '50px',
                }}
                key={index}
              >
                {(() => {
                  const orderNumber =
                    orderDetailsData?.orderNumber ??
                    orderDetailsData?.orderNumbers?.[0] ??
                    '';
                  return (
                    <PHOrderDetailsAndActions
                      emailId={orderDetailsData?.emailId ?? ''}
                      fulfillmentGroup={fulfillment as IFulfillmentGroup}
                      net={orderDetailsData?.net || ''}
                      orderNumber={orderNumber}
                      salesDate={orderDetailsData?.salesDate}
                      orderDetailsLoaded={orderDetailsLoaded}
                      variant="table"
                      isPTCOrder={orderDetailsData?.isPTCOrder}
                    />
                  );
                })()}
              </div>
            );
          },
        )}
        {canceledLineItems.length > 0 && (
          <div className="sui-flex sui-w-full sui-flex-row sui-gap-0 sui-min-w-[330px] md:sui-min-w-[516px]">
            <div className="sui-flex sui-flex-col sui-gap-[8px] sui-min-w-[260px]">
              <Typography
                variant="body-base"
                weight="bold"
                style={{
                  textTransform: 'capitalize',
                  marginBottom: '12px',
                }}
              >
                {`Canceled ${dateFormat(
                  canceledLineItems?.[0]?.statusUpdatedDate,
                  'MMMM d',
                )}`}
              </Typography>
            </div>

            <div className="sui-min-w-[300px]">
              <div
                className="sui-flex sui-items-center sui-relative sui-gap-[16px]"
                style={{
                  width: '48px',
                  height: '48px',
                }}
              >
                {canceledLineItems.map((lineItem: ILineItem, idx: number) => {
                  if (
                    (lineItem as ILineItem)?.parentLine?.relationType ===
                    'COMBO'
                  )
                    return null;

                  return (
                    <div
                      key={idx}
                      style={{
                        overflow: 'hidden',
                        minWidth: '48px',
                        height: '48px',
                      }}
                    >
                      <CardMedia
                        src={generateImageUrl(lineItem)}
                        alt={
                          lineItem
                            ? (lineItem as ILineItem)?.description
                            : 'Unavailable image'
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
